import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiService, DashboardStats, Transaction } from '../services/api';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import Loader from './Loader';
import EmptyState from './EmptyState';
import { useNotifications } from './NotificationSystem';
import { handleApiError } from '../utils/errorHandler';
import './Reports.css';

interface ReportFilters {
  fromDate: string;
  toDate: string;
  accountId?: number;
  type?: string;
  category?: string;
}

interface CategoryData {
  category: string;
  amount: number;
  percentage: number;
  transactionCount: number;
  [key: string]: any; // Add index signature for chart compatibility
}

const Reports: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('month');
  const [filters, setFilters] = useState<ReportFilters>({
    fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  });
  const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
  const { showError } = useNotifications();
  const showErrorRef = useRef(showError);
  const isFetchingRef = useRef(false);
  const activeRequestKeyRef = useRef<string>('');

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  // Keep report query params stable across renders unless filters actually change.
  const reportQueryParams = useMemo(() => ({
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    accountId: filters.accountId,
    type: filters.type,
    category: filters.category,
    pageSize: 1000
  }), [filters.fromDate, filters.toDate, filters.accountId, filters.type, filters.category]);

  const fetchReportData = useCallback(async () => {
    // CRITICAL: Don't make API calls if not authenticated
    if (!apiService.isAuthenticated()) {
      console.warn('Reports: Not authenticated, skipping API calls');
      setLoading(false);
      return;
    }

    const requestKey = JSON.stringify(reportQueryParams);
    if (isFetchingRef.current && activeRequestKeyRef.current === requestKey) {
      console.warn('Reports: Duplicate report request prevented');
      return;
    }

    isFetchingRef.current = true;
    activeRequestKeyRef.current = requestKey;

    try {
      setLoading(true);
      const [statsData, transactionsData] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getTransactions(reportQueryParams)
      ]);
      
      setStats(statsData);
      setTransactions(transactionsData.transactions);
      
      // Process category data
      const categoryMap = new Map<string, { amount: number; count: number }>();
      transactionsData.transactions.forEach(transaction => {
        if (transaction.type === 2) { // Only expenses
          const category = transaction.category || 'Uncategorized';
          const existing = categoryMap.get(category) || { amount: 0, count: 0 };
          categoryMap.set(category, {
            amount: existing.amount + transaction.amount,
            count: existing.count + 1
          });
        }
      });
      
      const totalExpenses = Array.from(categoryMap.values()).reduce((sum, item) => sum + item.amount, 0);
      const categoryArray = Array.from(categoryMap.entries()).map(([category, data]) => ({
        category,
        amount: data.amount,
        percentage: totalExpenses > 0 ? (data.amount / totalExpenses) * 100 : 0,
        transactionCount: data.count
      })).sort((a, b) => b.amount - a.amount);
      
      setCategoryData(categoryArray);
    } catch (error: any) {
      // If 401, the global handler will redirect - don't show error
      if (error.status === 401 || error.errorCode === 'UNAUTHORIZED') {
        console.log('Reports: 401 detected, redirecting to login');
        return;
      }
      const appError = handleApiError(error);
      showErrorRef.current('Loading Error', appError.message);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [reportQueryParams]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    const now = new Date();
    let fromDate: Date;
    
    switch (period) {
      case 'week':
        fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        fromDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        fromDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    
    setFilters((prev) => ({
      ...prev,
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: now.toISOString().split('T')[0]
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Description', 'Type', 'Category', 'Amount', 'Account'];
    const csvContent = [
      headers.join(','),
      ...transactions.map(t => [
        formatDate(t.entryDate),
        `"${t.description}"`,
        t.type === 1 ? 'Income' : t.type === 2 ? 'Expense' : 'Transfer',
        `"${t.category || 'Uncategorized'}"`,
        t.amount,
        `"${t.accountName}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${filters.fromDate}_to_${filters.toDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="reports">
        <Loader message="Loading reports..." size="large" />
      </div>
    );
  }

  return (
    <div className="reports">
      <div className="reports-header">
        <h1>Reports & Analytics</h1>
        <p>Analyze your financial data and generate insights</p>
        <div className="header-actions">
          <button className="btn-secondary" onClick={exportToCSV}>
            📊 Export CSV
          </button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="period-selector">
        <div className="period-buttons">
          {[
            { key: 'week', label: 'Last 7 Days' },
            { key: 'month', label: 'This Month' },
            { key: 'quarter', label: 'This Quarter' },
            { key: 'year', label: 'This Year' }
          ].map(period => (
            <button
              key={period.key}
              className={`period-btn ${selectedPeriod === period.key ? 'active' : ''}`}
              onClick={() => handlePeriodChange(period.key)}
            >
              {period.label}
            </button>
          ))}
        </div>
        
        <div className="date-range">
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
          />
          <span>to</span>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card total-income">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <h3>Total Income</h3>
            <p>{formatCurrency(stats?.monthlyIncome || 0)}</p>
          </div>
        </div>
        
        <div className="summary-card total-expenses">
          <div className="card-icon">📉</div>
          <div className="card-content">
            <h3>Total Expenses</h3>
            <p>{formatCurrency(stats?.monthlyExpenses || 0)}</p>
          </div>
        </div>
        
        <div className="summary-card net-income">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>Net Income</h3>
            <p className={stats && stats.netIncome >= 0 ? 'positive' : 'negative'}>
              {formatCurrency(stats?.netIncome || 0)}
            </p>
          </div>
        </div>
        
        <div className="summary-card total-transactions">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <h3>Transactions</h3>
            <p>{transactions.length}</p>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {transactions.length === 0 ? (
        <EmptyState
          icon="📊"
          title="No report data available"
          message="Add some transactions to see reports and analytics."
        />
      ) : (
        <>
      {/* Charts Section */}
      <div className="charts-section">
        <div className="charts-grid">
          {/* Income vs Expenses Chart */}
          <div className="chart-container">
            <h3>Income vs Expenses</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Income', amount: stats?.monthlyIncome || 0, fill: '#10b981' },
                { name: 'Expenses', amount: stats?.monthlyExpenses || 0, fill: '#ef4444' }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Bar dataKey="amount" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Category Breakdown Pie Chart */}
          <div className="chart-container">
            <h3>Expense Categories</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="amount"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Trend Chart */}
        <div className="chart-container full-width">
          <h3>Monthly Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={[
              { month: 'Jan', income: 45000, expenses: 35000 },
              { month: 'Feb', income: 48000, expenses: 32000 },
              { month: 'Mar', income: 50000, expenses: 38000 },
              { month: 'Apr', income: 52000, expenses: 40000 },
              { month: 'May', income: 48000, expenses: 36000 },
              { month: 'Jun', income: 55000, expenses: 42000 },
              { month: 'Jul', income: 50000, expenses: 38000 },
              { month: 'Aug', income: 53000, expenses: 39000 },
              { month: 'Sep', income: 51000, expenses: 37000 },
              { month: 'Oct', income: 54000, expenses: 41000 },
              { month: 'Nov', income: stats?.monthlyIncome || 0, expenses: stats?.monthlyExpenses || 0 },
              { month: 'Dec', income: 0, expenses: 0 }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Legend />
              <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2} name="Income" />
              <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} name="Expenses" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="category-breakdown">
        <div className="section-header">
          <h2>Expense Categories</h2>
          <p>Breakdown of your spending by category</p>
        </div>
        
        <div className="category-list">
          {categoryData.length === 0 ? (
            <EmptyState
              icon="📋"
              title="No category data"
              message="No expense categories found in the selected period."
            />
          ) : (
            categoryData.map((category, index) => (
            <div key={category.category} className="category-item">
              <div className="category-info">
                <div className="category-name">
                  <span className="category-rank">#{index + 1}</span>
                  <span className="category-title">{category.category}</span>
                </div>
                <div className="category-stats">
                  <span className="category-amount">{formatCurrency(category.amount)}</span>
                  <span className="category-percentage">{category.percentage.toFixed(1)}%</span>
                </div>
              </div>
              <div className="category-bar">
                <div 
                  className="category-fill" 
                  style={{ width: `${category.percentage}%` }}
                ></div>
              </div>
              <div className="category-details">
                <span>{category.transactionCount} transactions</span>
              </div>
            </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="recent-transactions">
        <div className="section-header">
          <h2>Recent Transactions</h2>
          <p>Latest transactions in the selected period</p>
        </div>
        
        <div className="transactions-table">
          <div className="table-header">
            <div className="col-date">Date</div>
            <div className="col-description">Description</div>
            <div className="col-category">Category</div>
            <div className="col-amount">Amount</div>
            <div className="col-account">Account</div>
          </div>
          
          <div className="table-body">
            {transactions.length === 0 ? (
              <EmptyState
                icon="📝"
                title="No transactions"
                message="No transactions found in the selected period."
              />
            ) : (
              transactions.slice(0, 20).map((transaction) => (
              <div key={transaction.id} className="table-row">
                <div className="col-date">{formatDate(transaction.entryDate)}</div>
                <div className="col-description">{transaction.description}</div>
                <div className="col-category">{transaction.category || 'Uncategorized'}</div>
                <div className={`col-amount ${transaction.type === 1 ? 'income' : transaction.type === 2 ? 'expense' : 'transfer'}`}>
                  {transaction.type === 1 ? '+' : transaction.type === 2 ? '-' : '↔'}{formatCurrency(transaction.amount)}
                </div>
                <div className="col-account">{transaction.accountName}</div>
              </div>
            ))
            )}
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="insights">
        <div className="section-header">
          <h2>Insights</h2>
          <p>Key insights from your financial data</p>
        </div>
        
        <div className="insights-grid">
          <div className="insight-card">
            <div className="insight-icon">💡</div>
            <div className="insight-content">
              <h3>Top Spending Category</h3>
              <p>
                {categoryData.length > 0 
                  ? `${categoryData[0].category} (${categoryData[0].percentage.toFixed(1)}% of expenses)`
                  : 'No expense data available'
                }
              </p>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">📊</div>
            <div className="insight-content">
              <h3>Average Transaction</h3>
              <p>
                {transactions.length > 0 
                  ? formatCurrency(transactions.reduce((sum, t) => sum + t.amount, 0) / transactions.length)
                  : 'No transaction data available'
                }
              </p>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">🎯</div>
            <div className="insight-content">
              <h3>Savings Rate</h3>
              <p>
                {stats && stats.monthlyIncome > 0 
                  ? `${stats.savingsRate.toFixed(1)}% of income saved`
                  : 'No income data available'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Reports;